import axios from 'axios';
import { 
    LoginResponse,
    APIError,
    Company,
    WatchedStock,
    Portfolio,
    Transaction,
    StockValue,
    Boutique,
    BoutiqueItems,
    SupplyOrders,
    SupplyItems,
    CustomerOrders
} from '../types/index';

const METHOD_GET = 'GET';
const METHOD_POST = 'POST';
const METHOD_PUT = 'PUT';
const METHOD_PATCH = 'PATCH';
const METHOD_DELETE = 'DELETE';

const BASE_URL = 'https://api.caramel.business'; //'http://127.0.0.1:8000';
const ERROR_MESSAGE = "We weren't able to reach the server, try again later";

type RequestMethod = typeof METHOD_GET | typeof METHOD_POST | typeof METHOD_PUT | typeof METHOD_PATCH | typeof METHOD_DELETE;

function _request<T>(method: RequestMethod, path: string, token: string = '', data: any = {}): Promise<T> {
    return new Promise((resolve, reject) => {
        const queryParams = method === 'GET' ? new URLSearchParams(data).toString() : '';
        axios({
            headers: token ? {'Authorization': `Token ${token}`} : {},
            method: method,
            url: `${BASE_URL}/${path}${queryParams ? `?${queryParams}` : ''}`,
            data: method === 'GET' ? {} : data,
        }).then(axiosResponse => {
            resolve(axiosResponse.data);
        }).catch(error => {
            console.log(error);
            if (error.response) {
                const data = error.response.data;
                const message =
                    (typeof data?.detail === 'string' && data.detail) ||
                    (typeof data?.error === 'string' && data.error) ||
                    (Array.isArray(data?.detail) && data.detail[0]) ||
                    'Request failed';
                reject(message);
            } else if (error.request) {
                reject({
                    code: 500,
                    message: ERROR_MESSAGE,
                    error: error.request
                } as APIError);
            } else {
                reject({
                    code: 500,
                    message: ERROR_MESSAGE,
                    error: error.message
                } as APIError);
            }
        });
    });
}

const login = (username: string, password: string): Promise<LoginResponse> => {
    return _request<LoginResponse>(METHOD_POST, 'api-auth/', '', { username, password });
}

const exchangeFirebaseToken = (firebaseToken: string): Promise<LoginResponse> => {
    return _request<LoginResponse>(METHOD_POST, 'api-auth/firebase/', '', { firebaseToken });
}

const verifyAuth = (token: string): Promise<LoginResponse> => {
    return _request<LoginResponse>(METHOD_GET, 'api-auth/verify/', token);
}

const getCompanies = (): Promise<Company[]> => {
    return _request<Company[]>(METHOD_GET, 'stocks/companies/');
}

const getIPOs = (): Promise<Company[]> => {
    return _request<Company[]>(METHOD_GET, 'stocks/ipos/');
}

const getWatchedStocks = (token: string): Promise<WatchedStock[]> => {
    return _request<WatchedStock[]>(METHOD_GET, 'stocks/watch/', token);
}

const createWatchedStock = (token: string, ticker: string, price: string, emailList: string, comparisonInfo: { comparison: 'less' | 'greater' }): Promise<WatchedStock> => {
    return _request<WatchedStock>(METHOD_POST, 'stocks/watch/', token, { ticker, price, emailList, comparisonInfo });
}

const updateWatchedStock = (token: string, pk: number, ticker: string, price: string, emailList: string, comparisonInfo: { comparison: 'less' | 'greater' }): Promise<WatchedStock> => {
    return _request<WatchedStock>(METHOD_PUT, `stocks/watch/${pk}/`, token, { ticker, price, emailList, comparisonInfo });
}

const deleteWatchedStock = (token: string, pk: number): Promise<void> => {
    return _request<void>(METHOD_DELETE, `stocks/watch/${pk}/`, token);
}

const getStockPortfolios = (token: string): Promise<Portfolio[]> => {
    return _request<Portfolio[]>(METHOD_GET, 'stocks/portfolio/', token);
}

const createStockPortfolio = (token: string, userID: number, name: string, participants: string): Promise<Portfolio> => {
    return _request<Portfolio>(METHOD_POST, 'stocks/portfolio/', token, { userID, name, participants });
}

const updateStockPortfolio = (token: string, pk: number, userID: number, name: string, participants: string): Promise<Portfolio> => {
    return _request<Portfolio>(METHOD_PUT, `stocks/portfolio/${pk}/`, token, { userID, name, participants });
}

const deleteStockPortfolio = (token: string, pk: number): Promise<void> => {
    return _request<void>(METHOD_DELETE, `stocks/portfolio/${pk}/`, token);
}

const getStockTransactions = (token: string): Promise<Transaction[]> => {
    return _request<Transaction[]>(METHOD_GET, 'stocks/transaction/', token);
}

const createStockTransaction = (token: string, userID: number, ticker: string, price: string, quantity: number, date: string, type: 'BUY' | 'SELL', portfolio?: number, note?: string): Promise<Transaction> => {
    return _request<Transaction>(METHOD_POST, 'stocks/transaction/', token, { userID, ticker, price, quantity, date, type, portfolio, note });
}

const updateStockTransaction = (token: string, pk: number, userID: number, ticker: string, price: string, quantity: number, date: string, portfolio?: number, note?: string): Promise<Transaction> => {
    return _request<Transaction>(METHOD_PATCH, `stocks/transaction/${pk}/`, token, { userID, ticker, price, quantity, date, portfolio, note });
}

const deleteStockTransaction = (token: string, pk: number): Promise<void> => {
    return _request<void>(METHOD_DELETE, `stocks/transaction/${pk}/`, token);
}

const getStockCurrentValues = (token: string): Promise<StockValue[]> => {
    return _request<StockValue[]>(METHOD_GET, 'stocks/currentvalues/', token);
}

const getPersonalTransactions = (token: string): Promise<any[]> => {
    return _request<any[]>(METHOD_GET, 'pfinance/transactions/', token);
}

const importPersonalTransactions = (token: string, defaultTags: string, jsonInput: string): Promise<void> => {
    return _request<void>(METHOD_POST, 'pfinance/transactions/import/', token, { defaultTags, jsonInput });
}

const deletePersonalTransaction = (token: string, pk: number): Promise<void> => {
    return _request<void>(METHOD_DELETE, `pfinance/transactions/${pk}/`, token);
}

const getUserTags = (token: string): Promise<any[]> => {
    return _request<any[]>(METHOD_GET, 'pfinance/tags/', token);
}

const deleteUserTag = (token: string, pk: number): Promise<void> => {
    return _request<void>(METHOD_DELETE, `pfinance/tags/${pk}/`, token);
}

const getBoutiques = (token: string): Promise<Boutique[]> => {
    return _request<Boutique[]>(METHOD_GET, 'boutique/boutiques/', token);
}

const getBoutiqueItems = (token: string, boutiqueId: number): Promise<BoutiqueItems[]> => {
    return _request<BoutiqueItems[]>(METHOD_GET, 'boutique/boutique-items/', token, { boutiqueId });
}

const createBoutiqueItem = (token: string, data: Partial<BoutiqueItems>): Promise<BoutiqueItems> => {
    return _request<BoutiqueItems>(METHOD_POST, 'boutique/boutique-items/', token, data);
};

const updateBoutiqueItem = (token: string, boutiqueItemId: number, data: Partial<BoutiqueItems>): Promise<BoutiqueItems> => {
    return _request<BoutiqueItems>(METHOD_PUT, `boutique/boutique-items/${boutiqueItemId}/`, token, data);
};

const deleteBoutiqueItem = (token: string, boutiqueItemId: number): Promise<void> => {
    return _request<void>(METHOD_DELETE, `boutique/boutique-items/${boutiqueItemId}/`, token);
};

const getSupplyOrders = (token: string, boutiqueId: number): Promise<SupplyOrders[]> => {
    return _request<SupplyOrders[]>(METHOD_GET, 'boutique/supply-orders/', token, { boutiqueId });
}

const createSupplyOrder = (token: string, data: Partial<SupplyOrders>): Promise<SupplyOrders> => {
    return _request<SupplyOrders>(METHOD_POST, 'boutique/supply-orders/', token, data);
};

const updateSupplyOrder = (token: string, supplyOrderId: number, data: Partial<SupplyOrders>): Promise<SupplyOrders> => {
    return _request<SupplyOrders>(METHOD_PUT, `boutique/supply-orders/${supplyOrderId}/`, token, data);
};

const deleteSupplyOrder = (token: string, supplyOrderId: number): Promise<void> => {
    return _request<void>(METHOD_DELETE, `boutique/supply-orders/${supplyOrderId}/`, token);
};

const getSupplyItems = (token: string, boutiqueId: number): Promise<SupplyItems[]> => {
    return _request<SupplyItems[]>(METHOD_GET, 'boutique/supply-items/', token, { boutiqueId });
}

const createSupplyItem = (token: string, data: Partial<SupplyItems>): Promise<SupplyItems> => {
    return _request<SupplyItems>(METHOD_POST, 'boutique/supply-items/', token, data);
};

const updateSupplyItem = (token: string, supplyItemId: number, data: Partial<SupplyItems>): Promise<SupplyItems> => {
    return _request<SupplyItems>(METHOD_PUT, `boutique/supply-items/${supplyItemId}/`, token, data);
};

const deleteSupplyItem = (token: string, supplyItemId: number): Promise<void> => {
    return _request<void>(METHOD_DELETE, `boutique/supply-items/${supplyItemId}/`, token);
};

const getCustomerOrders = (token: string, boutiqueId: number): Promise<CustomerOrders[]> => {
    return _request<CustomerOrders[]>(METHOD_GET, 'boutique/customer-orders/', token, { boutiqueId });
}

const createCustomerOrder = (token: string, data: Partial<CustomerOrders>): Promise<CustomerOrders> => {
    return _request<CustomerOrders>(METHOD_POST, 'boutique/customer-orders/', token, data);
};

const updateCustomerOrder = (token: string, customerOrderId: number, data: Partial<CustomerOrders>): Promise<CustomerOrders> => {
    return _request<CustomerOrders>(METHOD_PUT, `boutique/customer-orders/${customerOrderId}/`, token, data);
};

const deleteCustomerOrder = (token: string, customerOrderId: number): Promise<void> => {
    return _request<void>(METHOD_DELETE, `boutique/customer-orders/${customerOrderId}/`, token);
};

const API = {
    login,
    exchangeFirebaseToken,
    verifyAuth,
    getIPOs,
    getCompanies,
    getWatchedStocks,
    createWatchedStock,
    updateWatchedStock,
    deleteWatchedStock,
    getStockPortfolios,
    createStockPortfolio,
    updateStockPortfolio,
    deleteStockPortfolio,
    getStockTransactions,
    createStockTransaction,
    updateStockTransaction,
    deleteStockTransaction,
    getStockCurrentValues,
    getPersonalTransactions,
    importPersonalTransactions,
    deletePersonalTransaction,
    getUserTags,
    deleteUserTag,
    getBoutiques,
    getBoutiqueItems,
    createBoutiqueItem,
    getSupplyOrders,
    getSupplyItems,
    getCustomerOrders,
    updateBoutiqueItem,
    deleteBoutiqueItem,
    updateSupplyOrder,
    deleteSupplyOrder,
    updateSupplyItem,
    deleteSupplyItem,
    updateCustomerOrder,
    deleteCustomerOrder,
    createSupplyOrder,
    createSupplyItem,
    createCustomerOrder
};

export default API; 